import { call, put, takeEvery, all } from 'redux-saga/effects';
import { ICollectionResponse, IMethod, IRequestParams, IResponse } from '@dash/apiInstance';
import { t } from 'src/i18n';
import ApiInstance from 'src/helpers/apiInstance';
import { IActiveSkill, IDeniedSkill, IPendingSkill } from '@dash/skillApplication';
import { executeAction } from 'src/helpers/api';
import { sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import actionTypes from './actionTypes';
import notifications from '../../../../redux/notifications/actions';
import { ILoadSkillsPathParams, IProcessActionResponse } from './types';
import actions from './actions';
import talentActions from '../actions';
import {
  LoadActiveSkillsRequestAction,
  LoadDeniedSkillsRequestAction,
  LoadPendingSkillsRequestAction,
  ProcessActionRequestAction,
} from './actions.d';

const activeSkillsApi = new ApiInstance('workers/:id/skills/active');
const pendingSkillsApi = new ApiInstance('workers/:id/skills/pending');
const deniedSkillsApi = new ApiInstance('workers/:id/skills/denied');

function* loadActiveSkills({ payload }: LoadActiveSkillsRequestAction<ILoadSkillsPathParams, {}>) {
  const { id } = payload;

  try {
    const result: ICollectionResponse<IActiveSkill> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      activeSkillsApi.get,
      { pathParams: { id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result?.data?.records) {
      yield put(actions.loadActiveSkillsSuccess({ activeSkills: result?.data?.records }));
    } else {
      yield put(actions.loadActiveSkillsError());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadPendingSkills({ payload }: LoadPendingSkillsRequestAction<ILoadSkillsPathParams, {}>) {
  const { id } = payload;

  try {
    const result: ICollectionResponse<IPendingSkill> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      pendingSkillsApi.get,
      { pathParams: { id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result?.data?.records) {
      yield put(
        actions.loadPendingSkillsSuccess({
          pendingSkills: result?.data?.records,
          pendingSkillsActions: result?.data?.actions,
        }),
      );
    } else {
      yield put(actions.loadPendingSkillsError());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadDeniedSkills({ payload }: LoadDeniedSkillsRequestAction<ILoadSkillsPathParams, {}>) {
  const { id } = payload;

  try {
    const result: ICollectionResponse<IDeniedSkill> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      deniedSkillsApi.get,
      { pathParams: { id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result?.data?.records) {
      yield put(actions.loadDeniedSkillsSuccess({ deniedSkills: result?.data?.records }));
    } else {
      yield put(actions.loadDeniedSkillsError());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* processAction({ payload }: ProcessActionRequestAction<{}, {}>) {
  try {
    const { url, method, data, key, id } = payload;

    const params: { url?: string; method?: IMethod; data?: any } = { url, method };

    if (method?.toLowerCase() !== 'get') {
      params.data = data;
    }

    const result: IResponse<IProcessActionResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      executeAction,
      params,
      notifications.REQUEST_ERROR,
    );

    if (result?.status === 200) {
      switch (true) {
        case key === 'mark_as_eligible':
          yield put(
            notifications.addNotification({
              headline: t('dash.common.notification.success_headline'),
              type: 'success',
              message: t('dash.landingPage.newSkillsApplications.mark_as_eligible_success_text'),
            }),
          );

          yield put(talentActions.loadData({ id }));

          break;

        case key === 'mark_as_ineligible':
          yield put(
            notifications.addNotification({
              headline: t('dash.common.notification.success_headline'),
              type: 'success',
              message: t('dash.landingPage.newSkillsApplications.mark_as_ineligible_success_text'),
            }),
          );

          yield put(talentActions.loadData({ id }));

          break;

        case key === 'approve':
          yield put(
            notifications.addNotification({
              headline: t('dash.common.notification.success_headline'),
              type: 'success',
              message: t('dash.landingPage.newSkillsApplications.skill_approved_successful_text'),
            }),
          );

          yield put(actions.loadPendingSkillsRequest({ id }));
          yield put(actions.loadActiveSkillsRequest({ id }));

          break;

        case key === 'deny':
          yield put(
            notifications.addNotification({
              headline: t('dash.common.notification.success_headline'),
              type: 'success',
              message: t('dash.landingPage.newSkillsApplications.skill_denied_successful_text'),
            }),
          );

          yield put(actions.loadPendingSkillsRequest({ id }));
          yield put(actions.loadDeniedSkillsRequest({ id }));

          break;

        case key === 'mark_as_active':
          yield put(
            notifications.addNotification({
              headline: t('dash.common.notification.success_headline'),
              type: 'success',
              message: t('dash.landingPage.newSkillsApplications.skill_marked_as_active_successfully'),
            }),
          );

          yield put(actions.loadActiveSkillsRequest({ id }));
          yield put(actions.loadDeniedSkillsRequest({ id }));

          break;

        case key === 'request_interview':
          yield put(
            notifications.addNotification({
              headline: t('dash.common.notification.success_headline'),
              type: 'success',
              message: t('dash.landingPage.newSkillsApplications.requested_interview_successfully'),
            }),
          );

          yield put(actions.loadPendingSkillsRequest({ id }));

          break;

        default:
          yield put(
            notifications.addNotification({
              headline: t('dash.common.notification.success_headline'),
              type: 'success',
              message: result?.messages?.join(' ') || '',
            }),
          );
      }

      yield put(actions.processActionSuccess());
    } else {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );

      yield put({ type: actionTypes.PROCESS_ACTION_ERROR });
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actionTypes.LOAD_ACTIVE_SKILL_REQUEST, loadActiveSkills)]);
  yield all([takeEvery(actionTypes.LOAD_PENDING_SKILL_REQUEST, loadPendingSkills)]);
  yield all([takeEvery(actionTypes.LOAD_DENIED_SKILL_REQUEST, loadDeniedSkills)]);
  yield all([takeEvery(actionTypes.PROCESS_ACTION_REQUEST, processAction)]);
}
